import React, { useEffect, useMemo, useState } from 'react';

import { getEvents as getArchivedEvents, getUpcomingEvents } from '../../api';
import { filterEventsForTheme, getThemeRequestFilters } from '../../lib/discoveryThemes';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FALLBACK_LAT = 36.95;
const FALLBACK_LNG = -122.1;

function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseEventDate(item) {
  const raw = item?.starts_at || item?.start_ts || item?.start_date || '';
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeCalendarEvent(item) {
  const parsedDate = parseEventDate(item);
  if (!parsedDate) return null;

  return {
    id: item?.id || item?.item_type_id || item?._id || item?.title || item?.name,
    title: item?.title || item?.name || item?.event_type || 'Untitled cultural event',
    summary:
      item?.summary ||
      item?.description ||
      item?.reason ||
      'No short cultural summary is available yet.',
    status: item?.status || 'scheduled',
    date: parsedDate,
    dateKey: toDateKey(parsedDate),
    startsAtLabel: parsedDate.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    locationLabel: item?.locations || item?.location_name || 'Regional cultural context',
    whyItMatters: Array.isArray(item?.why_it_matters)
      ? item.why_it_matters
      : Array.isArray(item?.tags)
        ? item.tags
        : [],
  };
}

function buildMonthCells(monthDate) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const startWeekday = start.getDay();
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - startWeekday);

  return Array.from({ length: 42 }, (_value, index) => {
    const current = new Date(gridStart);
    current.setDate(gridStart.getDate() + index);
    return current;
  });
}

export const CulturalCalendar = ({ lat, lng }) => {
  const themeFilters = useMemo(() => getThemeRequestFilters('cultural'), []);
  const seedLat = Number.isFinite(lat) ? lat : FALLBACK_LAT;
  const seedLng = Number.isFinite(lng) ? lng : FALLBACK_LNG;
  const [monthDate, setMonthDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceMode, setSourceMode] = useState('live');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setSourceMode('live');

    getUpcomingEvents({
      lat: seedLat,
      lon: seedLng,
      radiusKm: 160,
      days: 365,
      interests: themeFilters.interests,
      categories: themeFilters.categories,
    })
      .then(({ data }) => {
        if (!active) return;
        const normalized = filterEventsForTheme(data?.events || [], 'cultural')
          .map(normalizeCalendarEvent)
          .filter(Boolean)
          .sort((left, right) => left.date.getTime() - right.date.getTime());
        setEvents(normalized);
        setSourceMode('live');
      })
      .catch(async () => {
        try {
          const { data } = await getArchivedEvents(1);
          if (!active) return;

          const normalized = filterEventsForTheme(data?.data || [], 'cultural')
            .map(normalizeCalendarEvent)
            .filter(Boolean)
            .sort((left, right) => left.date.getTime() - right.date.getTime());

          setEvents(normalized);
          setSourceMode('archive');
        } catch (fallbackError) {
          if (!active) return;
          setError(
            fallbackError?.response?.data?.error ||
              fallbackError?.message ||
              'Unable to load the cultural calendar right now.'
          );
        }
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [seedLat, seedLng, themeFilters.categories, themeFilters.interests]);

  useEffect(() => {
    if (!events.length) return;

    const matchingEvent = events.find(
      (item) =>
        item.date.getFullYear() === monthDate.getFullYear() &&
        item.date.getMonth() === monthDate.getMonth()
    );

    if (matchingEvent) {
      setSelectedDateKey((currentValue) => currentValue || matchingEvent.dateKey);
      return;
    }

    setSelectedDateKey(events[0].dateKey);
  }, [events, monthDate]);

  const eventsByDate = useMemo(() => {
    return events.reduce((collection, item) => {
      collection[item.dateKey] = collection[item.dateKey] || [];
      collection[item.dateKey].push(item);
      return collection;
    }, {});
  }, [events]);

  const monthCells = useMemo(() => buildMonthCells(monthDate), [monthDate]);
  const monthLabel = monthDate.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const selectedEvents = eventsByDate[selectedDateKey] || [];
  const selectedLabel = selectedEvents[0]
    ? selectedEvents[0].date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    : 'Select a date';

  return (
    <section className="atlas-sidebar-card atlas-cultural-calendar">
      <div className="atlas-sidebar-copy atlas-sidebar-copy--flush">
        <h3>Interactive cultural calendar</h3>
        <p>
          Browse dated cultural observances and seasonal-practice windows for the region in view,
          then open a day to see what is scheduled there.
        </p>
      </div>

      {sourceMode === 'archive' ? (
        <div className="atlas-detail-note">
          Live cultural dates are unavailable right now, so this calendar is reading from the
          published archive.
        </div>
      ) : null}

      {loading ? <div className="atlas-empty-state atlas-empty-state--compact">Loading cultural calendar…</div> : null}
      {!loading && error ? <div className="atlas-empty-state atlas-empty-state--compact">{error}</div> : null}

      {!loading && !error ? (
        <div className="atlas-cultural-calendar-shell">
          <div className="atlas-cultural-calendar-panel">
            <div className="atlas-cultural-calendar-toolbar">
              <button
                type="button"
                className="atlas-secondary-link atlas-control-button"
                onClick={() => setMonthDate((currentValue) => addMonths(currentValue, -1))}
              >
                Previous
              </button>
              <div className="atlas-cultural-calendar-heading">
                <strong>{monthLabel}</strong>
                <span>
                  {events.filter(
                    (item) =>
                      item.date.getFullYear() === monthDate.getFullYear() &&
                      item.date.getMonth() === monthDate.getMonth()
                  ).length}{' '}
                  dated items
                </span>
              </div>
              <button
                type="button"
                className="atlas-secondary-link atlas-control-button"
                onClick={() => setMonthDate((currentValue) => addMonths(currentValue, 1))}
              >
                Next
              </button>
            </div>

            <div className="atlas-cultural-calendar-weekdays">
              {WEEKDAY_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>

            <div className="atlas-cultural-calendar-grid">
              {monthCells.map((cell) => {
                const dateKey = toDateKey(cell);
                const dayEvents = eventsByDate[dateKey] || [];
                const isCurrentMonth = cell.getMonth() === monthDate.getMonth();
                const isSelected = dateKey === selectedDateKey;

                return (
                  <button
                    key={dateKey}
                    type="button"
                    className={[
                      'atlas-cultural-calendar-day',
                      isCurrentMonth ? '' : 'is-outside',
                      dayEvents.length ? 'has-events' : '',
                      isSelected ? 'is-selected' : '',
                    ]
                      .join(' ')
                      .trim()}
                    onClick={() => setSelectedDateKey(dateKey)}
                  >
                    <strong>{cell.getDate()}</strong>
                    {dayEvents.length ? <span>{dayEvents.length}</span> : <span>&nbsp;</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="atlas-cultural-calendar-detail">
            <p className="atlas-panel-label">{selectedLabel}</p>
            {selectedEvents.length ? (
              <div className="atlas-upcoming-grid atlas-upcoming-grid--single">
                {selectedEvents.map((item) => (
                  <article key={item.id} className="atlas-upcoming-card">
                    <h4>{item.title}</h4>
                    <p>{item.summary}</p>
                    <div className="atlas-detail-list">
                      <span>
                        <strong>When</strong>
                        {item.startsAtLabel}
                      </span>
                      <span>
                        <strong>Status</strong>
                        {item.status}
                      </span>
                      <span>
                        <strong>Context</strong>
                        {item.locationLabel}
                      </span>
                    </div>
                    {item.whyItMatters.length ? (
                      <ul className="atlas-bullet-list atlas-bullet-list--compact">
                        {item.whyItMatters.slice(0, 3).map((detail) => (
                          <li key={`${item.id}-${detail}`}>{detail}</li>
                        ))}
                      </ul>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <div className="atlas-empty-state atlas-empty-state--compact">
                Choose a highlighted date to inspect the cultural events scheduled there.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};
